import React, { useState, useEffect } from "react";
import { t } from "i18next";
import { getOrders } from "../../../api/orders";
import Loading from "../../../common/components/Loading";
import EmptyState from "../../../components/emptystate/EmptyState";

const STATUS_FILTERS = [
    { id: "all", labelKey: "all" },
    { id: "DRAFT", labelKey: "draft" },
    { id: "SUPPLIER_QUERY", labelKey: "waitingSupplier" },
    { id: "SUPPLIER_CONFIRMED", labelKey: "supplierConfirmed" },
    { id: "ORDER_CONFIRMED", labelKey: "orderConfirmed" },
    { id: "LOGISTICS_ENROUTE", labelKey: "logisticsEnroute" },
    { id: "DELIVERED", labelKey: "delivered" },
    { id: "PAID", labelKey: "paid" },
    { id: "CANCELLED", labelKey: "cancelled" },
];

const STATUS_BADGE = {
    DRAFT: { bg: "bg-gray-100", text: "text-gray-600" },
    SUPPLIER_QUERY: { bg: "bg-yellow-100", text: "text-yellow-700" },
    SUPPLIER_CONFIRMED: { bg: "bg-blue-100", text: "text-blue-700" },
    BUYER_NOTIFIED: { bg: "bg-indigo-100", text: "text-indigo-700" },
    LOGISTICS_SEARCH: { bg: "bg-purple-100", text: "text-purple-700" },
    LOGISTICS_ASSIGNED: { bg: "bg-violet-100", text: "text-violet-700" },
    ORDER_FORMED: { bg: "bg-cyan-100", text: "text-cyan-700" },
    ORDER_CONFIRMED: { bg: "bg-teal-100", text: "text-teal-700" },
    LOGISTICS_ENROUTE: { bg: "bg-orange-100", text: "text-orange-700" },
    LOADING: { bg: "bg-amber-100", text: "text-amber-700" },
    LOADED: { bg: "bg-lime-100", text: "text-lime-700" },
    DELIVERED: { bg: "bg-green-100", text: "text-green-700" },
    ACT_SIGNED: { bg: "bg-emerald-100", text: "text-emerald-700" },
    PAID: { bg: "bg-green-500", text: "text-white" },
    CANCELLED: { bg: "bg-red-100", text: "text-red-700" },
};

function MyOrders({ setActiveTab }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [expandedOrder, setExpandedOrder] = useState(null);

    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getOrders();
                setOrders(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err.message || "Ошибка загрузки заказов");
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const filteredOrders = statusFilter === "all"
        ? orders
        : orders.filter((o) => o.status === statusFilter);

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        return date.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatAmount = (amount) => {
        const num = Number(amount);
        return Number.isFinite(num) ? num.toLocaleString("ru-RU") : "—";
    };

    const getBadge = (status) => {
        const style = STATUS_BADGE[status] || { bg: "bg-gray-100", text: "text-gray-600" };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
                {t(status) || status}
            </span>
        );
    };

    const toggleExpand = (orderId) => {
        setExpandedOrder((prev) => (prev === orderId ? null : orderId));
    };

    return (
        <div className="w-full">
            <div className="bg-white rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900">{t("myOrders")}</h2>
                    <p className="text-sm text-gray-400 mt-0.5">
                        {orders.length} {t("ordersFound") || "заказов"}
                    </p>
                </div>

                {/* Status filters */}
                <div className="overflow-x-auto scrollbar-none border-b border-gray-100">
                    <div className="flex px-4 py-3 gap-2 min-w-max">
                        {STATUS_FILTERS.map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setStatusFilter(filter.id)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                                    statusFilter === filter.id
                                        ? "bg-[#147F26] text-white shadow-sm"
                                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                {t(filter.labelKey)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                {loading && (
                    <div className="flex justify-center py-16">
                        <Loading />
                    </div>
                )}

                {error && (
                    <div className="p-6">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && filteredOrders.length === 0 && (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <p className="text-gray-500 font-medium">{t("noOrders") || "Заказы не найдены"}</p>
                        <p className="text-gray-400 text-sm mt-1">
                            {statusFilter === "all"
                                ? t("noOrdersDesc") || "У вас пока нет заказов"
                                : t("noOrdersWithStatus") || "Нет заказов с выбранным статусом"}
                        </p>
                    </div>
                )}

                {!loading && !error && filteredOrders.length > 0 && (
                    <div className="divide-y divide-gray-50">
                        {filteredOrders.map((order) => (
                            <div key={order.id} className="p-5 hover:bg-gray-50/50 transition-colors">
                                {/* Main row */}
                                <div
                                    className="flex items-center gap-4 cursor-pointer"
                                    onClick={() => toggleExpand(order.id)}
                                >
                                    {/* Order icon */}
                                    <div className="w-12 h-12 rounded-2xl bg-[#147F26]/10 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-6 h-6 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                    </div>

                                    {/* Order info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-bold text-gray-900">Заказ #{order.id}</p>
                                            {getBadge(order.status)}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1">
                                            {formatDate(order.created_at)}
                                        </p>
                                    </div>

                                    {/* Amount */}
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-lg font-bold text-gray-900">
                                            {order.total_amount ? (
                                                <>
                                                    {formatAmount(order.total_amount)}{" "}
                                                    <span className="text-sm font-normal text-gray-400">
                                                        {order.currency || "₸"}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </p>
                                        {order.quantity && order.measurement_unit && (
                                            <p className="text-xs text-gray-400">
                                                {order.quantity} {order.measurement_unit}
                                            </p>
                                        )}
                                    </div>

                                    {/* Expand arrow */}
                                    <svg
                                        className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                                            expandedOrder === order.id ? "rotate-180" : ""
                                        }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>

                                {/* Expanded details */}
                                {expandedOrder === order.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                                        {order.pickup_address && (
                                            <div>
                                                <p className="text-xs text-gray-400 mb-1">{t("pickupAddress") || "Адрес погрузки"}</p>
                                                <p className="text-sm text-gray-700">{order.pickup_address}</p>
                                            </div>
                                        )}
                                        {order.delivery_address && (
                                            <div>
                                                <p className="text-xs text-gray-400 mb-1">{t("deliveryAddress") || "Адрес доставки"}</p>
                                                <p className="text-sm text-gray-700">{order.delivery_address}</p>
                                            </div>
                                        )}
                                        {order.loading_date && (
                                            <div>
                                                <p className="text-xs text-gray-400 mb-1">{t("loadingDate") || "Дата погрузки"}</p>
                                                <p className="text-sm text-gray-700">{formatDate(order.loading_date)}</p>
                                            </div>
                                        )}
                                        {order.delivery_date && (
                                            <div>
                                                <p className="text-xs text-gray-400 mb-1">{t("deliveryDate") || "Дата доставки"}</p>
                                                <p className="text-sm text-gray-700">{formatDate(order.delivery_date)}</p>
                                            </div>
                                        )}
                                        {order.agreed_price && (
                                            <div>
                                                <p className="text-xs text-gray-400 mb-1">{t("agreedPrice") || "Цена"}</p>
                                                <p className="text-sm text-gray-700">{formatAmount(order.agreed_price)} ₸</p>
                                            </div>
                                        )}
                                        {order.notes && (
                                            <div className="col-span-2">
                                                <p className="text-xs text-gray-400 mb-1">{t("notes") || "Примечания"}</p>
                                                <p className="text-sm text-gray-700">{order.notes}</p>
                                            </div>
                                        )}
                                        {order.act_file_url && (
                                            <div className="col-span-2">
                                                <p className="text-xs text-gray-400 mb-1">{t("actFile") || "Акт"}</p>
                                                <a
                                                    href={order.act_file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-[#147F26] hover:underline"
                                                >
                                                    {t("viewAct") || "Посмотреть акт"}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MyOrders;
